import {
  KIND_VALUES,
  LimitsReponse,
  OPERATIONS_VALUES,
  REMAINING_LIMITS_CATEGORY,
  RemainingLimitsParams,
  TIME_PERIOD_VALUES,
} from "@letsbit/malcolmlb";
import { useGetLimits } from "hooks/payments/useGetLimits";
import { useCurrencies, useCurrency } from "hooks/user/useCurrencies";
import { useCallback } from "react";
import { messages } from "./messages";
import { useFormatter } from "hooks/utils/useFormatter";
import { useIntl } from "react-intl";

export const useLimitsComponent = ({
  limitParams,
  period,
}: {
  limitParams: RemainingLimitsParams;
  period?: TIME_PERIOD_VALUES;
}) => {
  const { data: limits } = useGetLimits(limitParams);
  const currency = useCurrency(limitParams.currency);
  const { data: currencies } = useCurrencies();
  const { formatValue } = useFormatter();
  const intl = useIntl();

  const getInfo = useCallback(
    (limit: LimitsReponse) => {
      const percent =
        +limit.remaining < 0
          ? 1
          : (+limit.value - +limit.remaining) / +limit.value;
      const convertCurrency = currencies?.find(
        (c) => c.id === limit.convert_currency,
      );
      return {
        percent: percent,
        operationText:
          limit.operation === OPERATIONS_VALUES.WITHDRAW
            ? intl.formatMessage(messages.withdraw)
            : intl.formatMessage(messages.deposit),
        periodText:
          limit.time_period === TIME_PERIOD_VALUES.MONTHLY
            ? intl.formatMessage(messages.monthlySingular)
            : limit.time_period === TIME_PERIOD_VALUES.YEARLY
              ? intl.formatMessage(messages.yearlySingular)
              : intl.formatMessage(messages.dailySingular),
        limitText: formatValue(limit.value, currency?.precision),
        remainingText: formatValue(
          limit.category === REMAINING_LIMITS_CATEGORY.COIN_CONVERTED
            ? limit.convert_remaining
            : limit.remaining,
          currency?.precision,
        ),
        tickerIdText: currency?.ticker_id.toUpperCase(),
        currencyConverted: convertCurrency?.ticker_id.toUpperCase(),
        remainingConverted: formatValue(
          limit.remaining,
          convertCurrency?.precision,
        ),
        limitTypeText:
          limit.category === REMAINING_LIMITS_CATEGORY.OWN_ACCOUNT
            ? limit.operation === OPERATIONS_VALUES.WITHDRAW
              ? intl.formatMessage(messages.ownAccountsSend)
              : intl.formatMessage(messages.ownAccountsReceive)
            : limit.category === REMAINING_LIMITS_CATEGORY.THIRD_PARTY_ACCOUNT
              ? limit.operation === OPERATIONS_VALUES.WITHDRAW
                ? intl.formatMessage(messages.thirdPartSend)
                : intl.formatMessage(messages.thirdPartReceive)
              : "",
      };
    },
    [currencies, currency?.precision, currency?.ticker_id, formatValue, intl],
  );

  const limitsFiltered = limits?.filter(
    (l: LimitsReponse) =>
      l.time_period === period && l.kind === KIND_VALUES.VOLUME,
  );

  return {
    limits: limitsFiltered ?? [],
    getInfo,
  };
};
